import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BellIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { requestNotificationPermission, getNotificationStatus } from '../../push/initNotifications';
import { useTranslation } from 'react-i18next';
import api from '../../api/client';
import { useAuthStore } from '../../store/authStore';

const PROMPT_DISMISSED_KEY = 'notification-prompt-dismissed';
const PROMPT_DELAY_MS = 3000; // Wait 3 seconds after login before showing

interface NotificationPromptProps {
  isAuthenticated: boolean;
}

export default function NotificationPrompt({ isAuthenticated }: NotificationPromptProps) {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const { t } = useTranslation();
  const { user } = useAuthStore();

  useEffect(() => {
    if (!isAuthenticated) {
      setShowPrompt(false);
      return;
    }

    const checkNotificationStatus = async () => {
      // Check if already dismissed
      const dismissed = localStorage.getItem(PROMPT_DISMISSED_KEY);
      if (dismissed) return;

      // Check browser notification permission
      const status = getNotificationStatus();
      if (!status.supported) return;
      if (status.permission === 'denied') return;
      
      // Check if user has notifications disabled
      if (user && !user.notifications_enabled) {
        // Show prompt after delay if notifications are disabled
        const timer = setTimeout(() => {
          setShowPrompt(true);
        }, PROMPT_DELAY_MS);
        return () => clearTimeout(timer);
      }

      // Check if user has no push subscriptions
      try {
        const res = await api.get('/notifications/subscriptions');
        const subscriptions = res.data || [];
        const activeSubscriptions = subscriptions.filter((sub: any) => sub.is_active);
        
        // If permission is granted but no active subscriptions, show prompt
        if (status.permission === 'granted' && activeSubscriptions.length === 0) {
          const timer = setTimeout(() => {
            setShowPrompt(true);
          }, PROMPT_DELAY_MS);
          return () => clearTimeout(timer);
        }
        
        // If permission is not granted, show prompt
        if (status.permission !== 'granted') {
          const timer = setTimeout(() => {
            setShowPrompt(true);
          }, PROMPT_DELAY_MS);
          return () => clearTimeout(timer);
        }
      } catch (error) {
        // If we can't check subscriptions, still show prompt if permission not granted
        if (status.permission !== 'granted') {
          const timer = setTimeout(() => {
            setShowPrompt(true);
          }, PROMPT_DELAY_MS);
          return () => clearTimeout(timer);
        }
      }
    };

    checkNotificationStatus();
  }, [isAuthenticated, user]);

  const handleEnable = async () => {
    setIsRequesting(true);
    try {
      const success = await requestNotificationPermission();
      if (success) {
        setShowPrompt(false);
      } else {
        // Permission was denied
        localStorage.setItem(PROMPT_DISMISSED_KEY, 'denied');
        setShowPrompt(false);
      }
    } catch (error) {
      console.error('Failed to request notification permission:', error);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(PROMPT_DISMISSED_KEY, 'later');
    setShowPrompt(false);
  };

  return (
    <AnimatePresence>
      {showPrompt && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-overlay"
            onClick={handleDismiss}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 50 }}
            className="modal-content max-w-md"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="w-16 h-16 rounded-xl bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                <BellIcon className="w-8 h-8 text-primary-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-bold text-white mb-2">
                  {t('notifications.enableTitle', 'Enable Push Notifications')}
                </h2>
                <p className="text-sm text-surface-400">
                  {t('notifications.enableDescription', 'Get notified when family members add tasks, mention you, or share documents. Stay connected with your family!')}
                </p>
              </div>
              <button
                onClick={handleDismiss}
                className="btn-icon"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleDismiss}
                className="btn-secondary flex-1"
              >
                {t('notifications.later', 'Later')}
              </button>
              <button
                onClick={handleEnable}
                disabled={isRequesting}
                className="btn-primary flex-1"
              >
                {isRequesting ? 'Enabling...' : t('notifications.enable', 'Enable')}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

